import firebase from "config/firebase";
import {FirebaseError} from "firebase/app";
import {signInWithEmailAndPassword} from "firebase/auth";
import {handleError} from "helpers/errors";
import {FC, useMemo, useState} from "react";
import {useNavigate} from "react-router";

const AuthPage: FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const emailValid = email.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g);

  const onSignin = () => {
    if (!emailValid) return handleError("Please enter a valid email address");
    if (!password) return handleError("Please enter a password");
    setSubmitting(true);
    signInWithEmailAndPassword(firebase.auth, email, password).catch((err: FirebaseError) => {
      switch (err.code) {
        case "auth/invalid-email":
          handleError("Please enter a valid email address");
          break;
        case "auth/user-disabled":
          handleError("This account has been disabled");
          break;
        case "auth/user-not-found":
          handleError("This account does not exist");
          break;
        case "auth/wrong-password":
          handleError("Incorrect password");
          break;
        default:
          handleError("An unknown error occurred");
      }
      setSubmitting(false);
    });
  };

  const beenInApp = useMemo(() => {
    try {
      return localStorage.getItem("beenInApp") === "true";
    } catch {
      return true;
    }
  }, []);

  return (
    <div className="w-full bg-brand-cream h-full overflow-y-auto flex justify-center items-center">
      <div className="bg-white rounded-2xl divide-y divide-brand-border flex-cool h-max my-12 mx-4 max-w-[768px] min-w-[512px]">
        <header className="flex items-center justify-start gap-2.5 p-5">
          <img src="/akadian-logo-no-tagline.png" alt="Akadian" className="h-8 w-auto" />
          <span className="font-semibold text-2xl text-brand-navy grow mt-4">| Sign In</span>
          <span
            className={
              ["text-xs text-brand-amber hover:underline font-medium cursor-pointer", beenInApp && "hidden"].asClass
            }
            onClick={() => navigate("/auth/signup")}
          >
            Create an Account
          </span>
        </header>
        <main className="flex-cool p-5 gap-5">
          <div className="flex-cool gap-4">
            <input type="text" placeholder="Email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
            <div className="relative w-full">
              <input
                type={passwordVisible ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                className="w-full"
              />
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-brand-muted hover:text-brand-navy cursor-pointer select-none"
                onClick={() => setPasswordVisible(!passwordVisible)}
              >
                {passwordVisible ? "Hide" : "Show"}
              </span>
            </div>
          </div>
        </main>
        <footer className="p-5 flex items-center justify-between gap-2.5">
          <span
            className="text-sm text-brand-amber hover:underline font-medium cursor-pointer"
            onClick={() => navigate("/auth/forgot")}
          >
            Forgot Password
          </span>
          <button disabled={submitting} onClick={onSignin} className="btn-primary">
            Continue
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AuthPage;
